import { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

// Helper function to parse timestamp strings to seconds
const parseTimestamp = (timestamp: string): number => {
  // Handle format like "7:37" (MM:SS)
  if (timestamp.includes(':')) {
    const [minutes, seconds] = timestamp.split(':').map(Number);
    return minutes * 60 + seconds;
  }
  // Handle format like "457.2s"
  if (timestamp.endsWith('s')) {
    return parseFloat(timestamp.replace('s', ''));
  }
  // Default to 0 if format is unexpected
  return 0;
};

export default function NewsroomPage() {
  const [selectedVideo, setSelectedVideo] = useState<number | null>(null);

  const videos = [
    {
      id: 1,
      title: "Constitutional AI: Design Defect",
      description: "Understanding the fundamental design flaw in Constitutional AI systems",
      thumbnail: "https://public.readdy.ai/ai/img_res/3b860872bc3caf1204bf80f376afbf38.jpg",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      duration: "7:37",
      transcript: []
    },
    {
      id: 2,
      title: "The Path to Safe AI",
      description: "How Constitutional AI standards can protect civil rights",
      thumbnail: "https://readdy.ai/api/search-image?query=futuristic%20pathway%20leading%20to%20secure%20AI%20system%20with%20shield%20symbols%20and%20constitutional%20elements%20in%20clean%20minimalist%20design%20with%20blue%20and%20white%20tones%20showing%20safety%20and%20protection&width=800&height=450&seq=safe-ai-path-2&orientation=landscape",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      duration: "12:45"
    },
    {
      id: 3,
      title: "Luevano v. Campbell Explained",
      description: "The landmark case that's changing AI regulation",
      thumbnail: "https://readdy.ai/api/search-image?query=courtroom%20gavel%20with%20digital%20AI%20elements%20and%20justice%20scales%20in%20professional%20minimalist%20style%20with%20dark%20wood%20tones%20and%20clean%20white%20background%20representing%20legal%20technology%20case&width=800&height=450&seq=luevano-case-3&orientation=landscape",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      duration: "15:20"
    },
    {
      id: 4,
      title: "Why California Must Act Now",
      description: "The urgent need for AI safety legislation",
      thumbnail: "https://readdy.ai/api/search-image?query=California%20state%20capitol%20building%20with%20digital%20technology%20overlay%20and%20urgent%20warning%20symbols%20in%20clean%20minimalist%20design%20with%20golden%20state%20colors%20and%20white%20background&width=800&height=450&seq=ca-action-4&orientation=landscape",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      duration: "10:15"
    },
    {
      id: 5,
      title: "Engineering Trust in AI Systems",
      description: "Technical solutions for Constitutional AI implementation",
      thumbnail: "https://readdy.ai/api/search-image?query=engineering%20blueprint%20with%20AI%20trust%20mechanisms%20and%20security%20protocols%20in%20technical%20minimalist%20style%20with%20blue%20grid%20lines%20and%20clean%20white%20background%20showing%20system%20architecture&width=800&height=450&seq=eng-trust-5&orientation=landscape",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      duration: "18:30"
    },
    {
      id: 6,
      title: "The Future of AI Governance",
      description: "Building a framework for responsible AI development",
      thumbnail: "https://readdy.ai/api/search-image?query=futuristic%20governance%20structure%20with%20AI%20oversight%20mechanisms%20and%20policy%20frameworks%20in%20clean%20minimalist%20design%20with%20teal%20and%20white%20tones%20showing%20organized%20system&width=800&height=450&seq=ai-gov-6&orientation=landscape",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      duration: "14:55"
    }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* ACI Launch Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-megaphone-line text-xl"></i>
              <span className="font-semibold text-sm tracking-wide">MAJOR ANNOUNCEMENT</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              ACI Launch as California Think Tank
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              Introducing the AI Constitutional Institute - California's premier think tank dedicated to Constitutional AI standards and civil rights protection in the age of artificial intelligence
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/press-release-launch"
                className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Read Press Release
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <a
                href="/about"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Learn More About ACI
                <i className="ri-information-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Open Letter Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-alarm-warning-line text-xl"></i>
              <span className="font-semibold text-sm tracking-wide uppercase">URGENT CALL TO ACTION</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Open Letter to Governor Newsom and California Legislature
            </h2>
            
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              A civil rights message from the lead plaintiff in Luevano v. Campbell, calling for immediate action on Constitutional AI standards
            </p>
            
            <a
              href="/open-letter"
              className="inline-flex items-center gap-2 bg-white text-red-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-red-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Read Letter
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Video Explainers Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Video Explainers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Watch our comprehensive video series explaining Constitutional AI, design defects, and the path forward
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {videos.map((video, index) => (
              <div
                key={video.id}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                onClick={() => setSelectedVideo(index)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                      <i className="ri-play-fill text-3xl text-red-600"></i>
                    </div>
                  </div>
                  {video.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-gray-600">
                    {video.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo !== null && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-5xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative pt-[56.25%]">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={videos[selectedVideo].videoUrl}
                title={videos[selectedVideo].title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{videos[selectedVideo].title}</h2>
                  <p className="text-xl text-gray-600">{videos[selectedVideo].description}</p>
                </div>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-3xl"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Press Contact Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Press Inquiries</h2>
          <p className="text-xl text-gray-600 mb-8">
            For media inquiries, interview requests, or additional information about our work
          </p>
          <a 
            href="/#contact"
            className="inline-block bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            Contact Media Team
          </a>
        </div>
      </section>

      <Footer />

      {/* JSON-LD Schema for Video Objects */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": videos.map((video, index) => ({
            "@type": "VideoObject",
            "position": index + 1,
            "name": video.title,
            "description": video.description,
            "thumbnailUrl": video.thumbnail,
            "uploadDate": "2025-01-15",
            "duration": `PT${Math.floor(parseTimestamp(video.transcript?.[video.transcript.length - 1]?.timestamp || '0s') / 60)}M`,
            "transcript": video.transcript ? {
              "@type": "MediaObject",
              "encodingFormat": "text/plain",
              "text": video.transcript.map(t => `${t.speaker} [${t.timestamp}]: ${t.text}`).join('\n')
            } : undefined,
            "publisher": {
              "@type": "Organization",
              "name": "AI Policy Institute",
              "logo": {
                "@type": "ImageObject",
                "url": "https://readdy.ai/logo.png"
              }
            }
          }))
        })
      }} />
    </div>
  );
}